import { useCallback, useEffect, useState } from 'react';
import { apiJson, fetchAuthedBlob } from '../api/client.js';
import { FormRichTextarea } from './FormRichTextarea.jsx';
import { Modal } from './Modal.jsx';
import { formatDateTime } from '../utils/formatDate.js';

const FOLDERS = [
  { id: 'inbox', label: 'Inbox' },
  { id: 'sentitems', label: 'Sent' },
  { id: 'drafts', label: 'Drafts' },
];

function people(list) {
  if (!list?.length) return '';
  return list.map((person) => (person.name ? `${person.name} <${person.email}>` : person.email)).join(', ');
}

function messageStarter(signature) {
  const sign = (signature || '').trim();
  return sign ? `<p></p>${sign}` : '<p></p>';
}

function mailFormData(fields, files) {
  const data = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      data.append(key, value);
    }
  });
  files.forEach((file) => data.append('attachments[]', file));
  return data;
}

function rejectOversized(files) {
  const tooBig = files.find((file) => file.size > 3 * 1024 * 1024);
  if (tooBig) {
    return `${tooBig.name} is larger than 3 MB.`;
  }
  const total = files.reduce((sum, file) => sum + file.size, 0);
  if (total > 10 * 1024 * 1024) {
    return 'Attachments must be 10 MB or smaller altogether.';
  }
  return '';
}

function AttachmentPicker({ files, onChange }) {
  return (
    <div className="field">
      <span>Attachments</span>
      <input
        type="file"
        multiple
        onChange={(event) => {
          const picked = Array.from(event.target.files || []);
          onChange([...files, ...picked].slice(0, 8));
          event.target.value = '';
        }}
      />
      {files.length ? (
        <ul className="mail-file-list">
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`}>
              <span>{file.name}</span>
              <button type="button" className="btn-link small" onClick={() => onChange(files.filter((_, item) => item !== index))}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <span className="muted small">Up to 8 files, 3 MB each.</span>
      )}
    </div>
  );
}

async function saveAttachment(apiBase, messageId, attachment) {
  const params = new URLSearchParams({
    message_id: messageId,
    attachment_id: attachment.id,
  });
  const blob = await fetchAuthedBlob(`${apiBase}/attachment?${params}`);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = attachment.name || 'attachment';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * @param {{
 *   apiBase: string,
 *   messageId: string,
 *   canReply?: boolean,
 *   onSent?: () => void,
 *   signature?: string,
 * }} props
 */
export function MailMessageView({ apiBase, messageId, canReply = false, onSent, signature }) {
  const [message, setMessage] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null);
  const [body, setBody] = useState('');
  const [forwardTo, setForwardTo] = useState('');
  const [cc, setCc] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState('');
  const [ownSignature, setOwnSignature] = useState(signature || '');

  useEffect(() => {
    if (signature !== undefined) {
      setOwnSignature(signature);
      return undefined;
    }
    let cancelled = false;
    apiJson('/api/profile')
      .then((data) => {
        if (!cancelled) setOwnSignature(data.email_signature || '');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [signature]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setMessage(null);
    setMode(null);
    setBody('');
    setForwardTo('');
    setCc('');
    setFiles([]);
    setNotice('');
    const params = new URLSearchParams({ id: messageId });
    apiJson(`${apiBase}/message?${params}`)
      .then((data) => {
        if (cancelled) return;
        if (data.error && !data.message) {
          setError(data.error);
          return;
        }
        setMessage(data.message || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase, messageId]);

  const openComposer = (nextMode) => {
    setMode(nextMode);
    setBody(messageStarter(ownSignature));
    setForwardTo('');
    setCc('');
    setFiles([]);
    setNotice('');
    setError('');
  };

  const send = async (event) => {
    event.preventDefault();
    setSending(true);
    setError('');
    setNotice('');
    const sizeError = rejectOversized(files);
    if (sizeError) {
      setError(sizeError);
      setSending(false);
      return;
    }
    try {
      if (mode === 'forward') {
        await apiJson(`${apiBase}/forward`, {
          method: 'POST',
          body: mailFormData({ id: messageId, to: forwardTo, cc, body }, files),
        });
      } else {
        await apiJson(`${apiBase}/reply`, {
          method: 'POST',
          body: mailFormData({
            id: messageId,
            body,
            cc,
            all: mode === 'reply_all' ? '1' : '0',
          }, files),
        });
      }
      setBody('');
      setForwardTo('');
      setCc('');
      setFiles([]);
      setMode(null);
      setNotice('Sent from your Outlook mailbox.');
      onSent?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) return <p className="muted">Opening email…</p>;
  if (error && !message) return <p className="err small">{error}</p>;
  if (!message) return <p className="muted">This email could not be opened.</p>;

  return (
    <article className="mail-reader-card">
      <header>
        <h3 className="mail-subject">{message.subject}</h3>
        <p className="muted small">
          {message.from_name || message.from_email}
          {message.from_email ? ` · ${message.from_email}` : ''}
        </p>
        {message.to?.length ? <p className="muted small">To: {people(message.to)}</p> : null}
        {message.cc?.length ? <p className="muted small">Cc: {people(message.cc)}</p> : null}
        <p className="muted small">{formatDateTime(message.received_at)}</p>
      </header>
      <div className="mail-body" dangerouslySetInnerHTML={{ __html: message.body_html || '<p class="muted">This message has no body.</p>' }} />
      {message.attachments?.length ? (
        <ul className="mail-attachments">
          {message.attachments.map((attachment) => (
            <li key={attachment.id}>
              <button type="button" className="btn-link small" onClick={() => saveAttachment(apiBase, messageId, attachment).catch((err) => setError(err.message))}>
                {attachment.name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {notice ? <p className="muted small">{notice}</p> : null}
      {error ? <p className="err small">{error}</p> : null}
      {canReply ? (
        <div className="mail-actions">
          <button type="button" className="btn-secondary btn-sm" onClick={() => openComposer('reply')}>Reply</button>
          <button type="button" className="btn-secondary btn-sm" onClick={() => openComposer('reply_all')}>Reply all</button>
          <button type="button" className="btn-secondary btn-sm" onClick={() => openComposer('forward')}>Forward</button>
        </div>
      ) : null}
      {canReply && mode ? (
        <form className="stack mail-compose" onSubmit={send}>
          <p className="muted small" style={{ margin: 0 }}>
            {mode === 'forward' ? 'Forward' : mode === 'reply_all' ? 'Reply all' : `Reply to ${message.from_email || 'sender'}`}
            . Your text is added above the original message.
          </p>
          {mode === 'forward' ? (
            <label className="field">
              <span>To</span>
              <input value={forwardTo} onChange={(event) => setForwardTo(event.target.value)} placeholder="name@company.com, other@company.com" required />
            </label>
          ) : null}
          <label className="field">
            <span>Cc</span>
            <input value={cc} onChange={(event) => setCc(event.target.value)} placeholder="name@company.com, other@company.com" />
          </label>
          <FormRichTextarea label="Message" value={body} onChange={setBody} height={240} />
          <AttachmentPicker files={files} onChange={setFiles} />
          <div className="row-actions">
            <button type="submit" className="btn-primary" disabled={sending}>{sending ? 'Sending…' : 'Send'}</button>
            <button type="button" className="btn-secondary" onClick={() => setMode(null)} disabled={sending}>Cancel</button>
          </div>
        </form>
      ) : null}
    </article>
  );
}

/**
 * @param {{ apiBase: string, messageId: string | null, canReply?: boolean, onClose: () => void }} props
 */
export function MailMessageDialog({ apiBase, messageId, canReply = false, onClose }) {
  return (
    <Modal open={Boolean(messageId)} title="Email" onClose={onClose} size="xl">
      {messageId ? <MailMessageView apiBase={apiBase} messageId={messageId} canReply={canReply} /> : null}
    </Modal>
  );
}

export function MailClient() {
  const [folder, setFolder] = useState('inbox');
  const [draftQuery, setDraftQuery] = useState('');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [mailbox, setMailbox] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [listError, setListError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [composing, setComposing] = useState(false);
  const [compose, setCompose] = useState({ to: '', cc: '', subject: '', body: '<p></p>' });
  const [composeFiles, setComposeFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [composeError, setComposeError] = useState('');
  const [signature, setSignature] = useState('');
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [signatureSaving, setSignatureSaving] = useState(false);

  const load = useCallback(async (nextSkip, append) => {
    setListError('');
    if (!append) setLoading(true);
    try {
      const params = new URLSearchParams({ folder, skip: String(nextSkip) });
      if (query) params.set('q', query);
      const data = await apiJson(`/api/admin/emails?${params}`);
      if (data.error && !data.items?.length && !append) {
        setListError(data.error);
      }
      setMailbox(data.mailbox || '');
      setHasMore(Boolean(data.has_more));
      setItems((current) => (append ? [...current, ...(data.items || [])] : data.items || []));
    } catch (err) {
      setListError(err.message);
    } finally {
      setLoading(false);
    }
  }, [folder, query]);

  useEffect(() => {
    setSelectedId(null);
    setComposing(false);
    void load(0, false);
  }, [load]);

  useEffect(() => {
    apiJson('/api/profile')
      .then((data) => setSignature(data.email_signature || ''))
      .catch(() => {});
  }, []);

  const saveSignature = async () => {
    setSignatureSaving(true);
    setListError('');
    try {
      const saved = await apiJson('/api/profile', {
        method: 'PUT',
        body: { email_signature: signature },
      });
      setSignature(saved.email_signature || '');
      setSignatureOpen(false);
    } catch (err) {
      setListError(err.message);
    } finally {
      setSignatureSaving(false);
    }
  };

  const sendNew = async (event) => {
    event.preventDefault();
    setSending(true);
    setComposeError('');
    const sizeError = rejectOversized(composeFiles);
    if (sizeError) {
      setComposeError(sizeError);
      setSending(false);
      return;
    }
    try {
      await apiJson('/api/admin/emails/send', {
        method: 'POST',
        body: mailFormData(compose, composeFiles),
      });
      setCompose({ to: '', cc: '', subject: '', body: messageStarter(signature) });
      setComposeFiles([]);
      setComposing(false);
      if (folder === 'sentitems') {
        void load(0, false);
      } else {
        setFolder('sentitems');
      }
    } catch (err) {
      setComposeError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={selectedId || composing ? 'mail-client has-selection' : 'mail-client'}>
      <section className="mail-list" data-tour="mailbox-list">
        <div className="mail-toolbar">
          <div className="mail-toolbar-actions">
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={() => {
                setComposing(true);
                setSelectedId(null);
                setCompose({ to: '', cc: '', subject: '', body: messageStarter(signature) });
                setComposeFiles([]);
                setComposeError('');
              }}
            >
              New message
            </button>
            <button type="button" className="btn-secondary btn-sm" onClick={() => setSignatureOpen((open) => !open)}>
              Signature
            </button>
          </div>
          <div className="mail-folders" role="tablist" aria-label="Mailbox folders">
            {FOLDERS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={folder === item.id}
                className={folder === item.id ? 'btn-secondary btn-sm is-active' : 'btn-secondary btn-sm'}
                onClick={() => setFolder(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <form
          className="mail-search"
          onSubmit={(event) => {
            event.preventDefault();
            setQuery(draftQuery.trim());
          }}
        >
          <input className="form-control" value={draftQuery} onChange={(event) => setDraftQuery(event.target.value)} placeholder="Search this folder" aria-label="Search mail" />
          <button type="submit" className="btn-secondary">Search</button>
        </form>
        {signatureOpen ? (
          <div className="stack mail-signature">
            <FormRichTextarea label="Signature" value={signature} onChange={setSignature} height={180} />
            <div className="row-actions">
              <button type="button" className="btn-primary btn-sm" onClick={() => void saveSignature()} disabled={signatureSaving}>
                {signatureSaving ? 'Saving…' : 'Save signature'}
              </button>
            </div>
          </div>
        ) : null}
        {mailbox ? <p className="muted small">{mailbox}</p> : null}
        {listError ? <p className="err small">{listError}</p> : null}
        {loading ? <p className="muted">Loading emails…</p> : null}
        {!loading && !items.length ? <p className="muted">No messages in this folder.</p> : null}
        <ul className="list-plain">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className={selectedId === item.id ? 'mail-list-item is-active' : 'mail-list-item'}
                onClick={() => {
                  setSelectedId(item.id);
                  setComposing(false);
                  setItems((current) => current.map((row) => (row.id === item.id ? { ...row, is_read: true } : row)));
                }}
              >
                <strong className={item.is_read ? '' : 'is-unread'}>{item.subject}</strong>
                <span className="muted small">
                  {folder === 'sentitems'
                    ? (item.to_emails || []).join(', ') || 'Sent'
                    : item.from_name || item.from_email || 'Unknown'}
                  {item.has_attachments ? ' · attachment' : ''}
                </span>
                {item.preview ? <span className="muted small mail-preview">{item.preview}</span> : null}
                <span className="muted small">{formatDateTime(item.received_at)}</span>
              </button>
            </li>
          ))}
        </ul>
        {hasMore ? (
          <button type="button" className="btn-secondary btn-sm" onClick={() => load(items.length, true)}>
            Load more
          </button>
        ) : null}
      </section>
      <section className="mail-reader">
        {(selectedId || composing) ? (
          <button type="button" className="btn-link small mail-back" onClick={() => { setSelectedId(null); setComposing(false); }}>
            Back to list
          </button>
        ) : null}
        {composing ? (
          <form className="stack mail-compose" onSubmit={sendNew}>
            <h3 className="mail-subject">New message</h3>
            <label className="field">
              <span>To</span>
              <input value={compose.to} onChange={(event) => setCompose({ ...compose, to: event.target.value })} placeholder="name@company.com, other@company.com" required />
            </label>
            <label className="field">
              <span>Cc</span>
              <input value={compose.cc} onChange={(event) => setCompose({ ...compose, cc: event.target.value })} placeholder="name@company.com, other@company.com" />
            </label>
            <label className="field">
              <span>Subject</span>
              <input value={compose.subject} onChange={(event) => setCompose({ ...compose, subject: event.target.value })} required />
            </label>
            <FormRichTextarea label="Message" value={compose.body} onChange={(value) => setCompose({ ...compose, body: value })} height={280} />
            <AttachmentPicker files={composeFiles} onChange={setComposeFiles} />
            {composeError ? <p className="err small">{composeError}</p> : null}
            <div className="row-actions">
              <button type="submit" className="btn-primary" disabled={sending}>{sending ? 'Sending…' : 'Send'}</button>
              <button type="button" className="btn-secondary" onClick={() => setComposing(false)} disabled={sending}>Cancel</button>
            </div>
          </form>
        ) : selectedId ? (
          <MailMessageView
            apiBase="/api/admin/emails"
            messageId={selectedId}
            canReply
            signature={signature}
            onSent={() => load(0, false)}
          />
        ) : (
          <p className="muted">Select a message to read it here, or start a new one.</p>
        )}
      </section>
    </div>
  );
}
